import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Utensils, BedDouble, Clock, Star, ChefHat, ShieldCheck, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const heroImg = "https://images.unsplash.com/photo-1687648431656-da99da578d50?w=1600";

const categoryGallery = [
  { slug: "pizza", title: "Pizza", img: "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=600" },
  { slug: "biryani", title: "Biryani", img: "https://images.unsplash.com/photo-1705174299330-939dd03cc864?w=600" },
  { slug: "bbq", title: "BBQ", img: "https://images.unsplash.com/photo-1544025162-d76694265947?w=600" },
  { slug: "burgers", title: "Burgers", img: "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=600" },
  { slug: "shawarma", title: "Shawarma", img: "https://images.unsplash.com/photo-1662186342592-5b1236e95a4e?w=600" },
  { slug: "pasta", title: "Pasta", img: "https://images.unsplash.com/photo-1621996346565-e3dbc6d2d3b5?w=600" },
  { slug: "fries", title: "Fries & Sides", img: "https://images.unsplash.com/photo-1630384060421-cb20d0e0649d?w=600" },
  { slug: "drinks", title: "Drinks", img: "https://images.unsplash.com/photo-1437418747212-8d9709afab22?w=600" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
 useEffect(() => {
  api.get("/menu")
    .then((r) => {
      const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setFeatured(items.slice(0, 4));
    })
    .catch(() => setFeatured([]));
}, []);
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative hero-radial overflow-hidden grain" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-28 sm:pt-36 pb-16 sm:pb-24 grid lg:grid-cols-2 gap-10 items-center relative">
          <div>
            <span className="badge-gold rise">Besham · Pakistan</span>
            <h1 className="mt-5 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-white rise delay-1">
              A night of <span className="text-gold">gold</span>, <br />
              a plate of <span className="text-gold">moonlight</span>.
            </h1>
            <p className="mt-5 text-zinc-300 text-base sm:text-lg max-w-xl rise delay-2">
              Order wood-fired pizza, slow-cooked biryani and charcoal BBQ to your door — or reserve a quiet suite above the river.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 rise delay-3">
              <Link to="/menu" data-testid="hero-order-btn" className="btn-gold"><Utensils size={18} /> Order Food</Link>
              <Link to="/rooms" data-testid="hero-rooms-btn" className="btn-outline-gold"><BedDouble size={18} /> Book a Room</Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-xs text-zinc-400 font-mono uppercase tracking-widest rise delay-4">
              <span className="flex items-center gap-2"><ChefHat size={14} className="text-amber-400" /> House recipes</span>
              <span className="flex items-center gap-2"><Clock size={14} className="text-amber-400" /> Fast delivery</span>
              <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-amber-400" /> Manual verified pay</span>
            </div>
          </div>
          <div className="relative">
            <img src={heroImg} alt="Moon Hotel" className="rounded-2xl w-full aspect-[4/5] object-cover border border-amber-500/20 shadow-[0_30px_80px_rgba(0,0,0,0.6)] rise delay-2" />
            <div className="absolute -bottom-6 -left-4 sm:-left-8 card-lux p-4 w-56 hidden sm:block rise delay-4">
              <div className="flex items-center gap-2 mb-1"><Star size={14} className="text-amber-400 fill-amber-400" /><span className="text-sm text-white">Guest favourite</span></div>
              <div className="text-xs text-zinc-400">"The biryani tastes like a wedding feast."</div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="categories-section">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="badge-gold">Menu</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-white">Browse by craving</h2>
          </div>
          <Link to="/menu" className="hidden sm:inline-flex items-center gap-2 text-sm text-amber-300 hover:text-amber-200">See full menu <ArrowRight size={16} /></Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {categoryGallery.map((c) => (
            <Link
              key={c.slug}
              to={`/menu?category=${c.slug}`}
              data-testid={`cat-${c.slug}`}
              className="group relative overflow-hidden rounded-xl border border-zinc-800 hover:border-amber-500/50 transition-all"
            >
              <img src={c.img} alt={c.title} className="aspect-[4/5] object-cover w-full group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute left-3 bottom-3">
                <div className="text-white font-serif text-xl leading-none">{c.title}</div>
                <div className="text-[10px] text-amber-300 font-mono uppercase tracking-widest mt-1">Order now</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <span className="badge-gold">House picks</span>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-white mb-6">Signature dishes</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {featured.map((f) => (
              <Link key={f.item_id} to={`/menu?category=${f.category}`} className="card-lux overflow-hidden">
                <img src={f.image_url} alt={f.name} className="w-full aspect-square object-cover" />
                <div className="p-4">
                  <div className="text-white font-medium">{f.name}</div>
                  <div className="text-xs text-zinc-400 mt-1 line-clamp-2">{f.description}</div>
                  <div className="mt-3 text-amber-300 font-mono text-sm">from Rs {Number(f.price).toLocaleString("en-PK")}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ROOMS teaser */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="card-lux overflow-hidden grid md:grid-cols-2">
          <img src="https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=1200" alt="Suite" className="w-full h-full object-cover min-h-[260px]" />
          <div className="p-8 sm:p-10 flex flex-col justify-center">
            <span className="badge-gold w-fit">Stay with us</span>
            <h3 className="font-serif text-3xl text-white mt-3">Quiet suites above the river</h3>
            <p className="text-zinc-400 mt-3">Check availability, choose your dates, and send a request. Our team confirms within hours.</p>
            <Link to="/rooms" data-testid="rooms-teaser-btn" className="btn-outline-gold mt-6 w-fit"><BedDouble size={16} /> Check availability</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
